import { useState } from 'react';

const tiers = [
  {
    id: 'basic',
    name: 'Basic',
    price: 249,
    period: '/month',
    season: 'Mar – Nov',
    badge: null,
    description: 'Essential lawn care to keep your property looking tidy all season long.',
    color: 'white',
    features: [
      { label: 'Bi-weekly lawn mowing', included: true },
      { label: 'Lawn edging & trimming', included: true },
      { label: 'Grass clippings cleanup', included: true },
      { label: 'Property walkthrough', included: true },
      { label: 'Hedge & shrub trimming', included: false },
      { label: 'Garden bed weeding', included: false },
      { label: 'Seasonal fertilization', included: false },
      { label: 'Spring & fall cleanup', included: false },
      { label: 'Snow removal (winter)', included: false },
      { label: 'Aeration & overseeding', included: false },
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    price: 399,
    period: '/month',
    season: 'Mar – Nov',
    badge: 'Most Popular',
    description: 'Our most popular package — full seasonal care with no corners cut.',
    color: 'green',
    features: [
      { label: 'Weekly lawn mowing', included: true },
      { label: 'Lawn edging & trimming', included: true },
      { label: 'Grass clippings cleanup', included: true },
      { label: 'Property walkthrough', included: true },
      { label: 'Hedge & shrub trimming', included: true },
      { label: 'Garden bed weeding', included: true },
      { label: 'Seasonal fertilization', included: true },
      { label: 'Spring & fall cleanup', included: true },
      { label: 'Snow removal (winter)', included: false },
      { label: 'Aeration & overseeding', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 599,
    period: '/month',
    season: 'Year-round',
    badge: 'Best Value',
    description: 'Complete year-round property care — including snow removal all winter.',
    color: 'white',
    features: [
      { label: 'Weekly lawn mowing', included: true },
      { label: 'Lawn edging & trimming', included: true },
      { label: 'Grass clippings cleanup', included: true },
      { label: 'Property walkthrough', included: true },
      { label: 'Hedge & shrub trimming', included: true },
      { label: 'Garden bed weeding', included: true },
      { label: 'Seasonal fertilization', included: true },
      { label: 'Spring & fall cleanup', included: true },
      { label: 'Snow removal (winter)', included: true },
      { label: 'Aeration & overseeding', included: true },
    ],
  },
];

type BillingCycle = 'monthly' | 'yearly';

export default function PricingTiers() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');

  const getPrice = (base: number) =>
    billing === 'yearly' ? Math.round(base * 0.85) : base;

  return (
    <section id="packages" className="bg-white py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">SERVICE PACKAGES</p>
          <h2
            className="text-[#2B5741] text-4xl font-bold mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Choose Your Plan
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto mb-8">
            All packages include a dedicated crew, consistent scheduling, and our quality guarantee.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1 gap-0">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 text-xs font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                billing === 'monthly' ? 'bg-[#2B5741] text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-5 py-2 text-xs font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                billing === 'yearly' ? 'bg-[#2B5741] text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Yearly&nbsp;
              <span className={`text-[10px] font-bold ${billing === 'yearly' ? 'text-[#C8A97E]' : 'text-[#2B5741]'}`}>
                SAVE 15%
              </span>
            </button>
          </div>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const isGreen = tier.color === 'green';
            return (
              <div
                key={tier.id}
                className={`relative rounded-xl border-2 overflow-hidden flex flex-col transition-all ${
                  isGreen
                    ? 'border-[#2B5741] bg-[#2B5741]'
                    : 'border-gray-200 bg-white hover:border-[#2B5741]/40'
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div className="absolute top-4 right-4">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        isGreen ? 'bg-[#C8A97E] text-white' : 'bg-[#2B5741] text-white'
                      }`}
                    >
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Card header */}
                <div className={`px-6 pt-7 pb-5 border-b ${isGreen ? 'border-white/20' : 'border-gray-100'}`}>
                  <p
                    className={`text-xs font-bold uppercase tracking-[0.3em] mb-2 ${
                      isGreen ? 'text-[#C8A97E]' : 'text-[#2B5741]'
                    }`}
                  >
                    {tier.name}
                  </p>
                  <div className="flex items-end gap-1 mb-1">
                    <span
                      className={`text-4xl font-bold ${isGreen ? 'text-white' : 'text-gray-800'}`}
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      ${getPrice(tier.price)}
                    </span>
                    <span className={`text-sm mb-1.5 ${isGreen ? 'text-white/60' : 'text-gray-400'}`}>
                      {tier.period}
                    </span>
                  </div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isGreen ? 'text-white/50' : 'text-gray-400'}`}>
                    {tier.season}
                  </p>
                  <p className={`text-xs leading-relaxed ${isGreen ? 'text-white/75' : 'text-gray-500'}`}>
                    {tier.description}
                  </p>
                </div>

                {/* Feature list */}
                <div className="px-6 py-5 flex-1">
                  <ul className="space-y-3">
                    {tier.features.map((f) => (
                      <li key={f.label} className="flex items-center gap-3">
                        <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 rounded-full ${
                          f.included
                            ? isGreen ? 'bg-[#C8A97E]/30' : 'bg-[#2B5741]/10'
                            : 'bg-transparent'
                        }`}>
                          {f.included ? (
                            <i className={`ri-check-line text-xs font-bold ${isGreen ? 'text-[#C8A97E]' : 'text-[#2B5741]'}`}></i>
                          ) : (
                            <i className="ri-subtract-line text-xs text-gray-300"></i>
                          )}
                        </div>
                        <span
                          className={`text-xs ${
                            f.included
                              ? isGreen ? 'text-white' : 'text-gray-700'
                              : isGreen ? 'text-white/30' : 'text-gray-300'
                          }`}
                        >
                          {f.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="px-6 pb-7">
                  <a
                    href="/#contact"
                    className={`block w-full text-center py-3 text-xs font-bold uppercase tracking-[0.2em] rounded-md transition-colors whitespace-nowrap ${
                      isGreen
                        ? 'bg-[#C8A97E] text-white hover:bg-[#b8976a]'
                        : 'bg-[#2B5741] text-white hover:bg-[#3A7060]'
                    }`}
                  >
                    Get Started
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          All prices are in CAD and do not include HST. Custom quotes available for larger or commercial properties.
        </p>
      </div>
    </section>
  );
}
